// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Search } from '@carbon/react';
import { Close } from '@carbon/icons-react';
import { useAudit } from '../../hooks/useAlarms';
import { AUDIT_ACTORS } from '../../mocks/audit';
import type { AuditEntry } from '../../types/alarm';
import { PageHead, SevTag, EmptyState, Loading } from '../../components/shell/ScreenHelpers';
import './AuditScreen.css';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function AuditScreen() {
  const navigate = useNavigate();
  const [actor, setActor] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const { entries, loading } = useAudit({ query, actor });

  const detail: [string, React.ReactNode][] = selected
    ? [
        ['Alarm', [selected.alarmId, selected.ruleId].filter(Boolean).join(' · ') || undefined],
        ['Priority', selected.sev ? <SevTag sev={selected.sev} /> : undefined],
        ['Asset', selected.asset],
        ['Category', selected.category],
        ['Source system', selected.srcSystem],
        ['Bound SOP', selected.sopId],
      ]
    : [];

  return (
    <div className="hs-page">
      <PageHead title="Audit" dateline="System of record · immutable event trail" />

      <div className="hs-toolbar">
        {AUDIT_ACTORS.map(a => (
          <button
            key={a}
            type="button"
            className={`hs-pill${actor === a ? ' active' : ''}`}
            onClick={() => setActor(a)}
          >
            {cap(a)}
          </button>
        ))}
        <div className="aud-search">
          <Search
            size="lg"
            labelText="Search audit trail"
            placeholder="Search entries, assets, alarm IDs…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : entries.length === 0 ? (
        <EmptyState big="No audit entries." />
      ) : (
        <table className="aud-table">
          <thead>
            <tr>
              <th className="aud-col-time">Time</th>
              <th className="aud-col-actor">Actor</th>
              <th>Entry</th>
              <th className="aud-col-src">Source</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr
                key={e.id}
                className={selected?.id === e.id ? 'active' : ''}
                role="button"
                tabIndex={0}
                aria-label={`Open audit entry ${e.alarmId ?? e.id}`}
                onClick={() => setSelected(e)}
                onKeyDown={ev => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    setSelected(e);
                  }
                }}
              >
                <td className="aud-time">{e.t}</td>
                <td className="aud-actor">{e.whoLabel}</td>
                <td className="aud-entry">{e.text}</td>
                <td className="aud-src">{e.srcSystem ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <>
          <div className="aud-scrim" onClick={() => setSelected(null)} />
          <aside className="aud-panel" role="dialog" aria-label="Audit entry detail">
            <div className="aud-panel-head">
              <div className="aud-panel-title">{selected.whoLabel}</div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription="Close"
                renderIcon={Close}
                onClick={() => setSelected(null)}
              />
            </div>
            <div className="aud-panel-time">{selected.t}</div>
            <p className="aud-panel-text">{selected.text}</p>

            <dl className="aud-panel-meta">
              {detail.map(([k, v]) =>
                v ? (
                  <div key={k} className="aud-panel-metaitem">
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ) : null
              )}
            </dl>

            {selected.alarmId && (
              <Button size="md" onClick={() => navigate(`/alarms/${selected.alarmId}`)}>
                Go to source
              </Button>
            )}
          </aside>
        </>
      )}
    </div>
  );
}
